// Pulls the links and images out of a collected email, and notes which ones
// track the reader: an open tracker is a tiny or hidden image whose only job
// is to be loaded, and a tracked link goes through the mail tool's counter
// before the real page.
//
// The HTML is read with DOMParser, which builds the page without running or
// loading anything in it, so this never tells the sender the email was opened.

// Mail tools, found from the addresses their trackers and links use.
const TOOLS = [
  [/sendgrid\.net$/, "SendGrid"],
  [/(list-manage\.com|mailchimp\.com|mailchi\.mp)$/, "Mailchimp"],
  [/(klclick\d*\.com|klaviyo\.com|klaviyomail\.com)$/, "Klaviyo"],
  [/(maillist-manage\.(net|com|eu|in)|zcsend\.net)$/, "Zoho Campaigns"],
  [/awstrack\.me$/, "Amazon SES"],
  [/mandrillapp\.com$/, "Mandrill"],
  [/(hubspot\.com|hubspotlinks\.com|hs-sites\.com|hubspotemail\.net)$/, "HubSpot"],
  [/rs6\.net$/, "Constant Contact"],
  [/(createsend\d*\.com|cmail\d+\.com)$/, "Campaign Monitor"],
  [/(sendibt\d*\.com|sendibm\d*\.com|brevo\.com|sendinblue\.com)$/, "Brevo"],
  [/mailgun\.(org|net|com)$/, "Mailgun"],
  [/(convertkit-mail\d*\.com|ck\.page|kit\.com|convertkit\.com)$/, "Kit"],
  [/(activehosted\.com|acemlna\.com|acemlnb\.com|acemlnc\.com|acemlnd\.com)$/, "ActiveCampaign"],
  [/(mlsend\.com|mailerlite\.com)$/, "MailerLite"],
  [/(customeriomail\.com|customer\.io)$/, "Customer.io"],
  [/(spgo\.io|sparkpostmail\.com)$/, "SparkPost"],
  [/(mail\.beehiiv\.com|link\.mail\.beehiiv\.com)$/, "beehiiv"],
  [/(substack\.com|substackcdn\.com)$/, "Substack"],
  [/(contactanchor\.com)$/, "Contact Anchor"],
];

function hostOf(url) {
  try { return new URL(url).hostname.toLowerCase(); } catch (e) { return ""; }
}

function toolFor(host) {
  const found = TOOLS.find(([pattern]) => pattern.test(host));
  return found ? found[1] : "";
}

// Paths open trackers use: SendGrid /wf/open, Mailchimp /track/open,
// Klaviyo /o/<id>, Zoho .../open.gif, ours .../p.gif, and the usual names.
const OPEN_PATH = /(\/wf\/open|\/track\/open|\/open(\.gif|\.png|\.php|\.aspx)?$|\/o\/[\w-]{10,}$|\/p\.gif$|\/pixel|\/beacon|\/trk\/|open\.jsp|\/e\/o\/|\/I0\/)/i;
// Paths click counters use.
const CLICK_PATH = /(\/ls\/click|\/track\/click|\/click\/|\/c\/[\w-]{10,}|\/L0\/|\/CL0\/|\/e\/c\/|\/redirect|\/ss\/c\/)/i;
const CLICK_HOST = /^(click|clicks|links?|trk|track|tracking|email|e|t|ct|ctrk|url\d*)\./;

function pixels(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(String(value));
  return isNaN(n) ? null : n;
}

function styleSize(style, prop) {
  const m = new RegExp(`(?:^|;|\\s)${prop}\\s*:\\s*([\\d.]+)px`, "i").exec(style || "");
  return m ? parseFloat(m[1]) : null;
}

// trackerReason says why an image looks like an open tracker, or "".
export function trackerReason({ url, width, height, hidden }) {
  const tool = toolFor(hostOf(url));
  let path = "";
  try { path = new URL(url).pathname; } catch (e) { /* not a web address */ }
  const tiny = width != null && height != null && width <= 3 && height <= 3;
  const named = OPEN_PATH.test(path);
  if (named) return `its address is an open counter${tool ? ` (${tool})` : ""}`;
  if (tiny && hidden) return "a hidden 1×1 image";
  if (tiny) return `a ${width}×${height} image`;
  if (hidden) return "a hidden image";
  return "";
}

// destination finds where a tracked link really goes, when the link says so
// (a url= style setting, or Amazon's /L0/<address>/ form). "" when it doesn't.
export function destination(url) {
  let u;
  try { u = new URL(url); } catch (e) { return ""; }
  for (const key of ["url", "u", "redirect", "redirect_url", "target", "r", "link", "dest"]) {
    const v = u.searchParams.get(key);
    if (v && /^https?:\/\//i.test(v)) return v;
  }
  const aws = /\/L0\/(https?:%2F%2F[^/]+)/i.exec(u.pathname);
  if (aws) {
    try { return decodeURIComponent(aws[1]); } catch (e) { return ""; }
  }
  return "";
}

function linkKind(text, url) {
  const words = `${text} ${url}`.toLowerCase();
  if (/unsubscribe|opt[\s-]?out|preference|manage (your )?subscription/.test(words)) return "unsubscribe";
  if (/\b(confirm|verify|activate)\b|yes,? (subscribe|sign me up)/.test(text.toLowerCase())) return "confirm";
  return "";
}

// tracked: the link goes through a click counter first. On a known mail
// tool's address a counter-style path or host is enough; on any other site
// it takes both, so ordinary links like example.com/click/ aren't flagged.
function tracked(url) {
  const host = hostOf(url);
  let path = "";
  try { path = new URL(url).pathname; } catch (e) { /* not a web address */ }
  if (toolFor(host)) return CLICK_PATH.test(path) || CLICK_HOST.test(host) || !!destination(url);
  return CLICK_PATH.test(path) && CLICK_HOST.test(host);
}

function cssUrls(css) {
  const out = [];
  const re = /url\(\s*["']?([^"')]+)["']?\s*\)/gi;
  let m;
  while ((m = re.exec(css || ""))) out.push(m[1].trim());
  return out;
}

const WEB = /^https?:\/\//i;

// emailParts lists the email's links and images. Links with the same address
// are listed once, with how many times they appear. Trackers come first.
export function emailParts(html, text) {
  const links = new Map();
  const addLink = (url, label) => {
    if (!WEB.test(url)) return;
    const seen = links.get(url);
    if (seen) {
      seen.count += 1;
      if (!seen.text && label) seen.text = label;
      if (!seen.kind) seen.kind = linkKind(label, url);
      return;
    }
    const host = hostOf(url);
    links.set(url, {
      url, host, text: label, count: 1,
      kind: linkKind(label, url),
      tracked: tracked(url),
      tool: toolFor(host),
      goesTo: destination(url),
    });
  };

  const images = [];
  const seenImages = new Set();
  const addImage = (url, attrs) => {
    if (!WEB.test(url) || seenImages.has(url)) return;
    seenImages.add(url);
    const host = hostOf(url);
    const image = { url, host, tool: toolFor(host), ...attrs };
    image.tracker = trackerReason(image);
    images.push(image);
  };

  if (html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("a[href]").forEach((a) => {
      addLink(a.getAttribute("href").trim(), (a.textContent || a.getAttribute("title") || "").replace(/\s+/g, " ").trim() ||
        (a.querySelector("img[alt]") ? `image: ${a.querySelector("img[alt]").getAttribute("alt")}` : ""));
    });
    doc.querySelectorAll("img[src]").forEach((img) => {
      const style = img.getAttribute("style") || "";
      addImage(img.getAttribute("src").trim(), {
        alt: img.getAttribute("alt") || "",
        width: pixels(img.getAttribute("width")) ?? styleSize(style, "width"),
        height: pixels(img.getAttribute("height")) ?? styleSize(style, "height"),
        hidden: /display\s*:\s*none|visibility\s*:\s*hidden|opacity\s*:\s*0(\.0*)?\s*(;|$|!)/i.test(style),
      });
    });
    // Backgrounds: the old background= setting and CSS url(...).
    doc.querySelectorAll("[background]").forEach((el) => addImage(el.getAttribute("background").trim(), { background: true }));
    doc.querySelectorAll("[style]").forEach((el) => cssUrls(el.getAttribute("style")).forEach((u) => addImage(u, { background: true })));
    doc.querySelectorAll("style").forEach((el) => cssUrls(el.textContent).forEach((u) => addImage(u, { background: true })));
  } else if (text) {
    (text.match(/https?:\/\/[^\s<>"')\]]+/gi) || []).forEach((u) => addLink(u.replace(/[.,;:!?]+$/, ""), ""));
  }

  const trackers = images.filter((i) => i.tracker);
  images.sort((a, b) => (b.tracker ? 1 : 0) - (a.tracker ? 1 : 0));

  // The tool that sent it: whatever most of its trackers and links point at.
  const votes = new Map();
  [...trackers, ...Array.from(links.values()).filter((l) => l.tracked)].forEach((p) => {
    if (p.tool) votes.set(p.tool, (votes.get(p.tool) || 0) + 1);
  });
  const tool = Array.from(votes).sort((a, b) => b[1] - a[1]).map(([t]) => t)[0] || "";

  return { links: Array.from(links.values()), images, trackers: trackers.length, tool };
}
